import { getUserPermissions } from './permissionsHelper';
import { routeIsAccessibleFor } from './routeHelpers';
import { hasConversationPermission } from './conversationPermissions';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const SIDEBAR_PAGE_TTL = 2 * 60 * 1000;

const SIDEBAR_PAGE_IDS = {
  CONTACTS_DEFAULT: 'contacts-default',
  COMPANIES_DEFAULT: 'companies-default',
  NOTIFICATIONS_DEFAULT: 'notifications-default',
  CANNED_DEFAULT: 'canned-default',
  CAPTAIN_ASSISTANTS_DEFAULT: 'captain-assistants-default',
  CAPTAIN_RESPONSES_DEFAULT: 'captain-responses-default',
  CAPTAIN_DOCUMENTS_DEFAULT: 'captain-documents-default',
};

export const canPreloadSidebarResource = (
  store,
  permission,
  pagePermission
) => {
  const accountId = store.getters.getCurrentAccountId;
  const user = store.getters.getCurrentUser;
  if (!user?.accounts) return false;
  const featureFlag = {
    contact_manage: FEATURE_FLAGS.CRM,
    company_manage: FEATURE_FLAGS.COMPANIES,
    captain_manage: FEATURE_FLAGS.CAPTAIN,
    canned_response_manage: FEATURE_FLAGS.CANNED_RESPONSES,
  }[permission];
  const featureEnabled = store.getters['accounts/isFeatureEnabledonAccount'];
  if (featureFlag && featureEnabled && !featureEnabled(accountId, featureFlag))
    return false;
  return routeIsAccessibleFor(
    {
      meta: {
        permissions: ['administrator', 'agent', ...[permission].flat()],
        pagePermission,
      },
    },
    getUserPermissions(user, accountId)
  );
};

const canPreloadConversations = store => {
  const user = store.getters.getCurrentUser;
  if (!user?.accounts) return false;
  const accountId = store.getters.getCurrentAccountId;
  return (
    hasConversationPermission(user, accountId, 'conversation_view') &&
    canPreloadSidebarResource(
      store,
      [
        'conversation_page_access',
        'conversation_manage',
        'conversation_unassigned_manage',
        'conversation_participating_manage',
      ],
      [
        'page_conversations_all',
        'page_conversations_mentions',
        'page_conversations_participating',
        'page_conversations_unattended',
        'page_conversations_folders',
        'page_conversations_teams',
        'page_conversations_channels',
        'page_conversations_labels',
      ]
    )
  );
};

export const preloadSidebarDependencies = store => {
  const contacts = canPreloadSidebarResource(store, 'contact_manage', [
    'page_contacts_all',
    'page_contacts_active',
    'page_contacts_segments',
    'page_contacts_labels',
  ]);
  const conversations = canPreloadConversations(store);
  if (contacts || conversations) {
    store.dispatch('labels/get');
    store.dispatch('attributes/get');
  }
  if (
    conversations ||
    canPreloadSidebarResource(store, 'inbox_manage', 'page_inbox')
  ) {
    store.dispatch('inboxes/get');
  }
  if (canPreloadSidebarResource(store, 'inbox_manage', 'page_inbox')) {
    store.dispatch('notifications/unReadCount');
  }
  if (
    conversations ||
    canPreloadSidebarResource(store, 'settings_manage', 'page_settings_teams')
  ) {
    store.dispatch('teams/get');
  }
  if (conversations) store.dispatch('customViews/get', 'conversation');
  if (contacts) store.dispatch('customViews/get', 'contact');
};

const sidebarPageCache = new Map();
const inflightPreloads = new Map();
const ACELERES_FUNIL_FRAME_ID = 'aceleres-funil-preload-frame';
const buildAceleresFunilFrameSrc = () =>
  `/funil?aceleres_sso_retry=${Date.now()}`;

let preloadedScopeKey = null;
let funilFrameLoaded = false;

const getNow = () => Date.now();

const buildInflightKey = (pageId, cacheKey) => `${pageId}:${cacheKey}`;

const scheduleSoon = callback => {
  if (typeof window === 'undefined') return Promise.resolve().then(callback);

  return new Promise(resolve => {
    const runner = async () => {
      try {
        await callback();
      } finally {
        resolve();
      }
    };

    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(runner, { timeout: 300 });
      return;
    }

    window.setTimeout(runner, 0);
  });
};

const hideAceleresFunilFrame = frame => {
  Object.assign(frame.style, {
    position: 'fixed',
    left: '-10000px',
    top: '0',
    width: '1px',
    height: '1px',
    opacity: '0',
    pointerEvents: 'none',
    border: '0',
  });
  frame.setAttribute('aria-hidden', 'true');
  frame.tabIndex = -1;
};

const showAceleresFunilFrame = frame => {
  Object.assign(frame.style, {
    position: 'static',
    left: '',
    top: '',
    width: '100%',
    height: '100%',
    opacity: '1',
    pointerEvents: 'auto',
    border: '0',
    display: 'block',
  });
  frame.removeAttribute('aria-hidden');
  frame.removeAttribute('tabindex');
};

const getOrCreateAceleresFunilFrame = ({ onLoad, onError } = {}) => {
  if (typeof document === 'undefined') return null;

  let frame = document.getElementById(ACELERES_FUNIL_FRAME_ID);
  let isNewFrame = false;
  if (!frame) {
    frame = document.createElement('iframe');
    frame.id = ACELERES_FUNIL_FRAME_ID;
    frame.title = 'Carteirização';
    frame.loading = 'eager';
    hideAceleresFunilFrame(frame);
    document.body.appendChild(frame);
    isNewFrame = true;
  }

  frame.onload = () => {
    funilFrameLoaded = true;
    onLoad?.();
  };
  frame.onerror = () => {
    onError?.();
  };

  if (isNewFrame) {
    frame.src = buildAceleresFunilFrameSrc();
  }

  return frame;
};

export const refreshAceleresFunilFrame = frame => {
  if (!frame) return;

  funilFrameLoaded = false;
  frame.src = buildAceleresFunilFrameSrc();
};

export const preloadAceleresFunilFrame = (allowed = () => true) => {
  scheduleSoon(() => {
    if (allowed()) getOrCreateAceleresFunilFrame();
  });
};

export const mountAceleresFunilFrame = (
  container,
  { onLoad, onError } = {}
) => {
  if (!container) return false;

  const frame = getOrCreateAceleresFunilFrame({ onLoad, onError });
  if (!frame) return false;

  // O preloader pode ter executado o SSO antes de a sessão atual estar pronta
  // ou enquanto uma sessão anterior ainda estava no cookie. Reexecutar a
  // página ao abrir garante que a ponte Zoraxy -> Aceleres use os tokens atuais.
  refreshAceleresFunilFrame(frame);
  showAceleresFunilFrame(frame);
  container.appendChild(frame);

  if (funilFrameLoaded) {
    window.setTimeout(() => onLoad?.(), 0);
  }

  return true;
};

export const parkAceleresFunilFrame = () => {
  if (typeof document === 'undefined') return;

  const frame = document.getElementById(ACELERES_FUNIL_FRAME_ID);
  if (!frame) return;

  hideAceleresFunilFrame(frame);
  document.body.appendChild(frame);
};

export const markSidebarPageReady = (pageId, cacheKey) => {
  sidebarPageCache.set(pageId, {
    cacheKey,
    loadedAt: getNow(),
  });
};

export const isSidebarPageReady = (
  pageId,
  cacheKey,
  maxAgeMs = SIDEBAR_PAGE_TTL
) => {
  const cachedPage = sidebarPageCache.get(pageId);

  if (!cachedPage || cachedPage.cacheKey !== cacheKey) {
    return false;
  }

  return getNow() - cachedPage.loadedAt <= maxAgeMs;
};

const runPreloadTask = async ({
  pageId,
  cacheKey,
  load,
  allowed = () => true,
}) => {
  if (!allowed()) return Promise.resolve();
  const accessScopeKey = preloadedScopeKey;
  const inflightKey = `${accessScopeKey}:${buildInflightKey(pageId, cacheKey)}`;
  if (inflightPreloads.has(inflightKey)) {
    return inflightPreloads.get(inflightKey);
  }

  const pendingTask = (async () => {
    try {
      await load();
      if (allowed() && accessScopeKey === preloadedScopeKey) {
        markSidebarPageReady(pageId, cacheKey);
      }
    } catch (error) {
      // Ignore preload failures. Route navigation still uses the normal fetch.
    } finally {
      inflightPreloads.delete(inflightKey);
    }
  })();

  inflightPreloads.set(inflightKey, pendingTask);
  return pendingTask;
};

export const buildContactsDefaultSidebarPageKey = ({
  accountId,
  sortAttr = 'last_activity_at',
} = {}) =>
  JSON.stringify({ accountId: String(accountId || ''), page: 1, sortAttr });

export const ensureContactsDefaultSidebarPage = ({
  store,
  accountId,
  sortAttr = 'last_activity_at',
}) => {
  if (
    !canPreloadSidebarResource(store, 'contact_manage', 'page_contacts_all')
  ) {
    return Promise.resolve();
  }
  const pageId = SIDEBAR_PAGE_IDS.CONTACTS_DEFAULT;
  const cacheKey = buildContactsDefaultSidebarPageKey({ accountId, sortAttr });
  if (isSidebarPageReady(pageId, cacheKey)) return Promise.resolve();

  return runPreloadTask({
    pageId,
    cacheKey,
    allowed: () =>
      canPreloadSidebarResource(store, 'contact_manage', 'page_contacts_all'),
    load: () => store.dispatch('contacts/get', { page: 1, sortAttr }),
  });
};

export const buildCompaniesDefaultSidebarPageKey = ({ sort = 'name' } = {}) =>
  JSON.stringify({ page: 1, sort });

export const buildNotificationsDefaultSidebarPageKey = () =>
  JSON.stringify({ page: 1 });

export const buildCannedDefaultSidebarPageKey = () =>
  JSON.stringify({ scope: 'default' });

export const buildCaptainAssistantsSidebarPageKey = () =>
  JSON.stringify({ scope: 'default' });

export const buildCaptainResponsesSidebarPageKey = ({
  assistantId,
  page = 1,
  status = 'approved',
  search = '',
} = {}) =>
  JSON.stringify({ assistantId: Number(assistantId), page, status, search });

export const buildCaptainDocumentsSidebarPageKey = ({
  assistantId,
  page = 1,
} = {}) => JSON.stringify({ assistantId: Number(assistantId), page });

const resolveCaptainAssistantId = ({ store, preferredAssistantId }) => {
  const assistants = store.getters['captainAssistants/getRecords'] || [];
  if (!assistants.length) return null;

  const normalizedPreferredId = Number(preferredAssistantId);
  const preferredAssistant = assistants.find(
    assistant => assistant.id === normalizedPreferredId
  );

  if (preferredAssistant) {
    return preferredAssistant.id;
  }

  return assistants[0]?.id || null;
};

export const preloadSidebarPages = ({
  store,
  companiesStore,
  uiSettings = {},
  scopeKey = 'default',
} = {}) => {
  const user = store.getters.getCurrentUser;
  const permissions = user?.accounts
    ? getUserPermissions(user, store.getters.getCurrentAccountId)
    : [];
  const accessScopeKey = JSON.stringify([scopeKey, user?.id, permissions]);
  if (preloadedScopeKey === accessScopeKey) return;
  sidebarPageCache.clear();
  preloadedScopeKey = accessScopeKey;

  scheduleSoon(async () => {
    if (accessScopeKey !== preloadedScopeKey) return;
    const companiesKey = buildCompaniesDefaultSidebarPageKey({
      sort: uiSettings.companies_sort_by || 'name',
    });
    const notificationsKey = buildNotificationsDefaultSidebarPageKey();
    const cannedKey = buildCannedDefaultSidebarPageKey();
    const assistantsKey = buildCaptainAssistantsSidebarPageKey();

    await Promise.allSettled([
      ensureContactsDefaultSidebarPage({
        store,
        accountId: scopeKey,
        sortAttr: uiSettings.contacts_sort_by || 'last_activity_at',
      }),
      runPreloadTask({
        pageId: SIDEBAR_PAGE_IDS.COMPANIES_DEFAULT,
        allowed: () =>
          canPreloadSidebarResource(
            store,
            'company_manage',
            'page_companies_all'
          ),
        cacheKey: companiesKey,
        load: () =>
          companiesStore.get({
            page: 1,
            sort: uiSettings.companies_sort_by || 'name',
          }),
      }),
      runPreloadTask({
        pageId: SIDEBAR_PAGE_IDS.NOTIFICATIONS_DEFAULT,
        allowed: () =>
          canPreloadSidebarResource(store, 'inbox_manage', 'page_inbox'),
        cacheKey: notificationsKey,
        load: () => store.dispatch('notifications/get', { page: 1 }),
      }),
      runPreloadTask({
        pageId: SIDEBAR_PAGE_IDS.CANNED_DEFAULT,
        allowed: () =>
          canPreloadSidebarResource(store, 'canned_response_manage', [
            'page_canned_responses',
            'page_settings_canned_responses',
          ]),
        cacheKey: cannedKey,
        load: () => store.dispatch('getCannedResponse'),
      }),
      runPreloadTask({
        pageId: SIDEBAR_PAGE_IDS.CAPTAIN_ASSISTANTS_DEFAULT,
        allowed: () =>
          canPreloadSidebarResource(store, 'captain_manage', [
            'page_captain_faqs',
            'page_captain_documents',
            'page_captain_settings',
            'page_captain_scenarios',
            'page_captain_playground',
            'page_captain_inboxes',
            'page_captain_tools',
          ]),
        cacheKey: assistantsKey,
        load: () => store.dispatch('captainAssistants/get'),
      }),
    ]);

    const assistantId = resolveCaptainAssistantId({
      store,
      preferredAssistantId: uiSettings.last_active_assistant_id,
    });

    if (!assistantId) return;

    await Promise.allSettled([
      runPreloadTask({
        pageId: SIDEBAR_PAGE_IDS.CAPTAIN_RESPONSES_DEFAULT,
        allowed: () =>
          canPreloadSidebarResource(
            store,
            'captain_manage',
            'page_captain_faqs'
          ),
        cacheKey: buildCaptainResponsesSidebarPageKey({ assistantId }),
        load: async () => {
          await store.dispatch('captainResponses/get', {
            assistantId,
            page: 1,
            status: 'approved',
          });
          if (
            !canPreloadSidebarResource(
              store,
              'captain_manage',
              'page_captain_faqs'
            )
          )
            return;
          await store.dispatch(
            'captainResponses/fetchPendingCount',
            assistantId
          );
        },
      }),
      runPreloadTask({
        pageId: SIDEBAR_PAGE_IDS.CAPTAIN_DOCUMENTS_DEFAULT,
        allowed: () =>
          canPreloadSidebarResource(
            store,
            'captain_manage',
            'page_captain_documents'
          ),
        cacheKey: buildCaptainDocumentsSidebarPageKey({ assistantId }),
        load: () =>
          store.dispatch('captainDocuments/get', {
            assistantId,
            page: 1,
          }),
      }),
    ]);
  });
};

export { SIDEBAR_PAGE_IDS };
